import React, { useState, useEffect } from 'react';
import {
  Typography, Button, Box, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, MenuItem, Select, FormControl, InputLabel,
  Autocomplete, Divider,
} from '@mui/material';
import { surgeryService, otRoomService, patientService, userService, surgeryTypeService, surgeryTypeMappingService } from '../services/api';

export const STATUS_OPTIONS = ['Scheduled', 'InProgress', 'Completed', 'Cancelled', 'Postponed'];
const TEAM_ROLES = ['AssistantSurgeon', 'Anesthetist', 'ScrubNurse', 'OTTechnician'];

// Format a Date (or ISO string) to the value expected by datetime-local inputs: YYYY-MM-DDTHH:MM
// Uses local clock so the displayed time matches what the user sees on screen.
const toLocalDateTimeInput = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = {
  patientId: null,
  surgeonId: null,
  otRoomId: null,
  surgeryTypeId: null,
  surgeryName: '',
  scheduledDateTime: '',
  estimatedDurationMinutes: 60,
  status: 'Scheduled',
  notes: '',
  teamMembers: [],
};

const emptyTeamMember = { userId: null, role: 'Anesthetist' };

// Create/Edit Surgery dialog, shared between the Surgery Planner and the IPD Full Record page.
// fixedPatient (optional): { id, firstName, lastName, phoneNumber } — pre-selects and locks the
// patient field when scheduling a new surgery from a patient's own record.
const SurgeryFormDialog = ({ open, editingItem, fixedPatient, prefillDateTime, onClose, onSaved }) => {
  const [otRooms, setOtRooms] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [surgeryTypes, setSurgeryTypes] = useState([]);
  const [typeMapping, setTypeMapping] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // patient search
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');

  // team member row
  const [newMember, setNewMember] = useState(emptyTeamMember);

  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        const [oRes, dRes, stRes] = await Promise.all([
          otRoomService.getAll(),
          userService.getDoctors(),
          surgeryTypeService.getAll(),
        ]);
        setOtRooms(oRes.data.filter(r => r.isActive));
        setDoctors(dRes.data);
        setSurgeryTypes(stRes.data.filter(t => t.isActive));
      } catch {}
    })();

    setTypeMapping(null);
    setPatientOptions([]);
    setPatientSearch('');
    setNewMember(emptyTeamMember);
    setError('');
    setConflicts([]);
    setSubmitting(false);

    if (editingItem) {
      const dt = editingItem.scheduledDateTime
        ? toLocalDateTimeInput(editingItem.scheduledDateTime)
        : '';
      setFormData({
        patientId: editingItem.patient ? { id: editingItem.patient.id, firstName: editingItem.patient.firstName, lastName: editingItem.patient.lastName, phoneNumber: editingItem.patient.phoneNumber } : null,
        surgeonId: editingItem.surgeon ? { id: editingItem.surgeon.id, firstName: editingItem.surgeon.firstName, lastName: editingItem.surgeon.lastName } : null,
        otRoomId: editingItem.otRoom ? editingItem.otRoom.id : null,
        surgeryTypeId: editingItem.surgeryType ? editingItem.surgeryType.id : null,
        surgeryName: editingItem.surgeryName,
        scheduledDateTime: dt,
        estimatedDurationMinutes: editingItem.estimatedDurationMinutes,
        status: editingItem.status,
        notes: editingItem.notes || '',
        teamMembers: (editingItem.teamMembers || []).map(tm => ({
          userId: { id: tm.user.id, firstName: tm.user.firstName, lastName: tm.user.lastName, role: tm.user.role },
          role: tm.role,
        })),
      });
      if (editingItem.surgeryType?.id) {
        surgeryTypeMappingService.get(editingItem.surgeryType.id).then(r => setTypeMapping(r.data)).catch(() => {});
      }
    } else {
      const dt = prefillDateTime
        ? toLocalDateTimeInput(prefillDateTime)
        : '';
      setFormData({ ...emptyForm, scheduledDateTime: dt, patientId: fixedPatient || null });
    }
  }, [open, editingItem, fixedPatient, prefillDateTime]);

  useEffect(() => {
    if (!patientSearch || patientSearch.length < 2) { setPatientOptions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await patientService.searchPatients(patientSearch);
        setPatientOptions(res.data);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch]);

  const handleSurgeryTypeChange = async (typeId) => {
    setFormData(prev => ({ ...prev, surgeryTypeId: typeId || null }));
    setTypeMapping(null);
    if (typeId) {
      try { const r = await surgeryTypeMappingService.get(typeId); setTypeMapping(r.data); } catch {}
    }
  };

  const addTeamMember = () => {
    if (!newMember.userId || !newMember.role) return;
    if (formData.teamMembers.some(tm => tm.userId?.id === newMember.userId?.id)) {
      setError('This person is already in the team.');
      return;
    }
    setFormData(prev => ({ ...prev, teamMembers: [...prev.teamMembers, { ...newMember }] }));
    setNewMember(emptyTeamMember);
    setError('');
  };

  const removeTeamMember = (idx) =>
    setFormData(prev => ({ ...prev, teamMembers: prev.teamMembers.filter((_, i) => i !== idx) }));

  const buildPayload = () => ({
    patientId: formData.patientId?.id,
    surgeonId: formData.surgeonId?.id,
    otRoomId: formData.otRoomId || null,
    surgeryTypeId: formData.surgeryTypeId || null,
    surgeryName: formData.surgeryName,
    scheduledDateTime: formData.scheduledDateTime || null,
    estimatedDurationMinutes: Number(formData.estimatedDurationMinutes),
    status: formData.status,
    notes: formData.notes || null,
    teamMembers: formData.teamMembers.map(tm => ({ userId: tm.userId?.id, role: tm.role })),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setConflicts([]);
    if (!formData.patientId) { setError('Please select a patient.'); return; }
    if (!formData.surgeonId) { setError('Please select a primary surgeon.'); return; }
    if (!formData.surgeryName.trim()) { setError('Surgery name is required.'); return; }
    if (!formData.scheduledDateTime) { setError('Scheduled date/time is required.'); return; }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editingItem) {
        await surgeryService.update(editingItem.id, payload);
      } else {
        await surgeryService.create(payload);
      }
      onSaved();
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && Array.isArray(data?.conflicts) && data.conflicts.length > 0) {
        setError(data.message || 'Scheduling conflict detected.');
        setConflicts(data.conflicts);
      } else {
        setError(data?.message || 'Failed to save surgery');
      }
      setSubmitting(false);
    }
  };

  const patientLocked = !editingItem && !!fixedPatient;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editingItem ? 'Edit Surgery' : 'Schedule Surgery'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Typography color="error" sx={{ mb: conflicts.length > 0 ? 1 : 2 }}>{error}</Typography>}
          {conflicts.length > 0 && (
            <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, color: 'error.main' }}>
              {conflicts.map((c, i) => (
                <Typography key={i} component="li" variant="body2" color="error">{c}</Typography>
              ))}
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={patientOptions}
                getOptionLabel={(o) => `${o.firstName} ${o.lastName} — ${o.phoneNumber}`}
                value={formData.patientId}
                onChange={(_, val) => setFormData(prev => ({ ...prev, patientId: val }))}
                onInputChange={(_, val) => setPatientSearch(val)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                disabled={patientLocked}
                renderInput={(params) => (
                  <TextField {...params} label="Patient *" placeholder="Search by name or phone" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Surgery Name"
                value={formData.surgeryName}
                onChange={(e) => setFormData(prev => ({ ...prev, surgeryName: e.target.value }))}
                placeholder="e.g. Appendectomy, LSCS, Hernia Repair"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={doctors}
                getOptionLabel={(o) => `Dr. ${o.firstName} ${o.lastName}${o.specialization ? ` — ${o.specialization}` : ''}`}
                value={formData.surgeonId}
                onChange={(_, val) => setFormData(prev => ({ ...prev, surgeonId: val }))}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Primary Surgeon *" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>OT Room</InputLabel>
                <Select
                  value={formData.otRoomId || ''}
                  label="OT Room"
                  onChange={(e) => setFormData(prev => ({ ...prev, otRoomId: e.target.value || null }))}
                >
                  <MenuItem value="">— Not Assigned —</MenuItem>
                  {otRooms.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Surgery Type</InputLabel>
                <Select
                  value={formData.surgeryTypeId || ''}
                  label="Surgery Type"
                  onChange={(e) => handleSurgeryTypeChange(e.target.value || null)}
                >
                  <MenuItem value="">— Not Specified —</MenuItem>
                  {surgeryTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Auto-populate preview when surgery type with mapping is selected on new surgery */}
            {!editingItem && formData.surgeryTypeId && typeMapping && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                  <Typography variant="caption" fontWeight={700} color="primary.dark" sx={{ display: 'block', mb: 0.75 }}>
                    Auto-creates on save:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(typeMapping.consentTemplates || []).map(t => (
                      <Chip key={t.id} size="small" label={t.name} color="primary" variant="outlined" />
                    ))}
                    {typeMapping.requireLabReports && <Chip size="small" label="Lab Reports" color="success" variant="outlined" />}
                    {typeMapping.requireXRay && <Chip size="small" label="X-Ray" color="success" variant="outlined" />}
                    {typeMapping.requireECG && <Chip size="small" label="ECG" color="success" variant="outlined" />}
                    {typeMapping.requireBloodGroup && <Chip size="small" label="Blood Group" color="success" variant="outlined" />}
                    {typeMapping.requireAnesthesiaFitness && <Chip size="small" label="Anesthesia Fitness" color="success" variant="outlined" />}
                    {typeMapping.requireConsentObtained && <Chip size="small" label="Consent Check" color="success" variant="outlined" />}
                    {typeMapping.requireIVLine && <Chip size="small" label="IV Line" color="success" variant="outlined" />}
                    {typeMapping.requirePatientFasting && <Chip size="small" label="Patient Fasting" color="success" variant="outlined" />}
                    {(!typeMapping.consentTemplates || typeMapping.consentTemplates.length === 0) &&
                     !typeMapping.requireLabReports && !typeMapping.requireXRay && !typeMapping.requireECG && !typeMapping.requireBloodGroup &&
                     !typeMapping.requireAnesthesiaFitness && !typeMapping.requireConsentObtained && !typeMapping.requireIVLine && !typeMapping.requirePatientFasting && (
                      <Typography variant="caption" color="text.secondary">No items configured for this type yet.</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Scheduled Date & Time" type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={formData.scheduledDateTime}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduledDateTime: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Estimated Duration (minutes)" type="number"
                value={formData.estimatedDurationMinutes}
                onChange={(e) => setFormData(prev => ({ ...prev, estimatedDurationMinutes: e.target.value }))}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Notes" multiline rows={2}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Pre-operative notes, special instructions..."
              />
            </Grid>

            {/* Team Members */}
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Surgical Team (optional)</Typography>
              </Divider>
            </Grid>
            {formData.teamMembers.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.teamMembers.map((tm, idx) => (
                    <Chip
                      key={idx}
                      label={`${tm.role}: ${tm.userId?.firstName} ${tm.userId?.lastName}`}
                      onDelete={() => removeTeamMember(idx)}
                      color="primary" variant="outlined" size="small"
                    />
                  ))}
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={7}>
              <Autocomplete
                options={doctors}
                getOptionLabel={(o) => `${o.firstName} ${o.lastName}${o.role ? ` (${o.role})` : ''}`}
                value={newMember.userId}
                onChange={(_, val) => setNewMember(prev => ({ ...prev, userId: val }))}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Team Member" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={newMember.role}
                  label="Role"
                  onChange={(e) => setNewMember(prev => ({ ...prev, role: e.target.value }))}
                >
                  {TEAM_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button fullWidth variant="outlined" onClick={addTeamMember} sx={{ height: '40px' }}>
                Add
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Saving…' : (editingItem ? 'Update' : 'Schedule')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SurgeryFormDialog;
